import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { buildEmptyItemsArray, usePaginationConfig } from '@yme/atoms/pagination';
import { DEFAULT_PER_PAGE_VALUES } from '@yme/common/constants';
import { useQueryParams } from '@yme/organisms/test-ride-grid/useQueryParams';
import { useTranslation } from '@yme/services/i18n';
import { getAdaptivePerPageLimit, getQueryParamByName } from '@yme/utils/common';
import { DRIVER_LICENCE_CONFIG, PARAMS_KEYS } from './constants';
import { getFilteredProductsByLicence, getDriversLicenseConfig, getUnitsPerPage, checkAllParamsCorrect, checkPerPageCorrect, checkDriverLicenseCorrect, } from './helpers';
export const useTestRideGrid = ({ isShowDriverLicenseFilter, productData, isMobileView, }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const width = window.innerWidth;
    const defaultDriverLicence = DRIVER_LICENCE_CONFIG[0].value;
    const defaultPerPageLimit = useMemo(() => {
        return getAdaptivePerPageLimit(width);
    }, [width]);
    const { getQueryParams, updateQueryParams } = useQueryParams({
        isShowDriverLicenseFilter,
    });
    const { perPage, page } = getQueryParams();
    const [demoUnits, setDemoUnits] = useState([]);
    const [isInitial, setIsInitial] = useState(false);
    const [prevSearch, setPrevSearch] = useState(location.search);
    const [config, setConfig] = useState({
        page: page ? page : 1,
        perPage: checkPerPageCorrect(perPage) ? perPage : defaultPerPageLimit,
        driverLicense: defaultDriverLicence,
        total: 0,
        isDefault: true,
    });
    const history = useHistory();
    const paginationConfigData = {
        currentPage: config.page,
        perPageOptions: DEFAULT_PER_PAGE_VALUES,
        activeElement: config.perPage,
        countItems: Math.floor(config.total / config.perPage),
        items: buildEmptyItemsArray(Math.ceil(demoUnits.length ? config.total / config.perPage : 1)),
    };
    const { paginationConfig, activeItemValue, onClickItemHandler, currentPerPageValue, setCurrentPerPageValue, setActiveItemValue, } = usePaginationConfig({
        paginationData: paginationConfigData,
        isMobileView,
    });
    const filterOnChangeHandler = useCallback((value) => {
        updateQueryParams({
            driverLicense: value,
            page: 1,
            perPage: config.perPage,
        });
    }, [config.perPage, updateQueryParams]);
    useEffect(() => {
        const { page, perPage, driverLicense } = getQueryParams();
        if (location.search &&
            checkAllParamsCorrect({
                page,
                perPage,
                driverLicense,
                isShowDriverLicenseFilter,
            })) {
            setIsInitial(true);
        }
        else {
            const isBrokenPerPage = perPage && !checkPerPageCorrect(perPage);
            const isBrokenDriverLicense = driverLicense && !checkDriverLicenseCorrect(driverLicense);
            updateQueryParams({
                perPage: isBrokenPerPage ? defaultPerPageLimit : undefined,
                driverLicense: isBrokenDriverLicense ? defaultDriverLicence : undefined,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if ((location.search !== prevSearch || isInitial) && !config.isDefault) {
            const { page, perPage, driverLicense } = getQueryParams();
            const filteredByLicenseProducts = getFilteredProductsByLicence(driverLicense, productData);
            const filteredTotal = filteredByLicenseProducts && filteredByLicenseProducts?.length;
            const total = filteredTotal || productData.length;
            setConfig((prevData) => ({
                ...prevData,
                perPage: perPage || defaultPerPageLimit,
                page,
                driverLicense,
                total,
            }));
            setIsInitial(false);
            setPrevSearch(location.search);
        }
    }, [
        config.isDefault,
        config.driverLicense,
        defaultPerPageLimit,
        getQueryParams,
        isInitial,
        isShowDriverLicenseFilter,
        location.search,
        prevSearch,
        productData,
        updateQueryParams,
    ]);
    useEffect(() => {
        if (productData.length && productData.length !== config.total) {
            getDriversLicenseConfig(t('productsGrid_allDriversLicenses_option'));
            const filteredByLicenseProducts = getFilteredProductsByLicence(config.driverLicense, productData);
            const filteredTotal = filteredByLicenseProducts && filteredByLicenseProducts?.length;
            setConfig((prevData) => ({
                ...prevData,
                total: filteredTotal || productData.length,
                isDefault: false,
            }));
        }
    }, [config.driverLicense, config.total, productData, t]);
    useEffect(() => {
        if (productData.length && config.total && !config.isDefault) {
            const filteredByLicenseProducts = getFilteredProductsByLicence(config.driverLicense, productData);
            const currentProducts = getUnitsPerPage(filteredByLicenseProducts || productData, config.page, config.perPage);
            setDemoUnits(currentProducts);
        }
    }, [config, productData]);
    useEffect(() => {
        if (!config.isDefault && activeItemValue !== config.page) {
            updateQueryParams({
                page: activeItemValue,
                perPage: config.perPage || defaultPerPageLimit,
            });
        }
    }, [
        activeItemValue,
        config.isDefault,
        config.page,
        config.perPage,
        defaultPerPageLimit,
        getQueryParams,
        updateQueryParams,
    ]);
    useEffect(() => {
        const queryPageParam = getQueryParamByName(history.location.search, PARAMS_KEYS.page);
        if (queryPageParam) {
            setActiveItemValue(Number(queryPageParam));
        }
    }, [history.location.search, setActiveItemValue]);
    useEffect(() => {
        if (!config.isDefault && currentPerPageValue !== config.perPage) {
            updateQueryParams({
                perPage: currentPerPageValue,
                page: 1,
            });
        }
    }, [config, config.isDefault, currentPerPageValue, getQueryParams, updateQueryParams]);
    return {
        config,
        demoUnits,
        paginationConfigData,
        paginationConfig,
        activeItemValue,
        onClickItemHandler,
        setCurrentPerPageValue,
        filterOnChangeHandler,
    };
};
